import datasetsConfig from '@/data/datasets.json'

/**
 * Past this many ids (every history together) the restricted URL risks the
 * usual 8 KB request-line limit (one id per record and history travels in
 * `where`), so the restriction is dropped and the chart counts every status
 * change, as it did before.
 */
export const MAX_CURRENT_STATUS_IDS = 600

const tableOf = column => String(column).trim().split('.')[0]

const parseWhere = (raw) => {
  if (!raw) return {}
  try {
    const where = JSON.parse(raw)
    return where && typeof where === 'object' && !Array.isArray(where) ? where : {}
  } catch (e) {
    return {}
  }
}

/** Named filter params of the dataset whose column lives in one of `tables`. */
const paramsOnTables = (datasetKey, tables) => {
  const filters = datasetsConfig[datasetKey]?.filters || {}
  return Object.keys(filters).filter(param => tables.includes(tableOf(filters[param])))
}

const readColumn = (row, column) => (
  row?.[column] !== undefined ? row[column] : row?.[String(column).split('.').pop()]
)

/**
 * The histories a module declares: `{ historyColumn, tables }` once, or a
 * `histories` list when the dataset joins more than one (plannings).
 */
export function historiesOf(config) {
  if (Array.isArray(config?.histories)) return config.histories
  return config?.historyColumn ? [{ historyColumn: config.historyColumn, tables: config.tables || [] }] : []
}

const historyTablesOf = config => historiesOf(config).flatMap(history => history.tables)

const readsTables = (params, datasetKey, tables) => {
  const onTables = column => tables.includes(tableOf(column))
  const listed = ['by', 'columns'].flatMap(key => String(params.get(key) || '').split(','))
  return listed.some(onTables)
    || Object.keys(parseWhere(params.get('where'))).some(onTables)
    || paramsOnTables(datasetKey, tables).some(param => params.has(param))
}

/**
 * The histories a request reads: in its grouping, its columns, its `where` or
 * a named filter param. Only those multiply the rows, so only those are worth
 * their extra read.
 */
export function referencedHistories(params, datasetKey, config) {
  if (!params) return []
  return historiesOf(config).filter(history => readsTables(params, datasetKey, history.tables))
}

export const DEFAULT_CURRENT_STATUS_LABEL = 'Solo estatus actual'

/**
 * Rewrite a chart or CSV URL into a read of `columns` over the records in
 * scope. Filters on any history are dropped: with «Estatus = in_review»
 * applied, the read would only see the in_review rows and take the last of
 * those instead of the current one.
 */
function buildScopeReadUrl(url, datasetKey, config, columns) {
  if (!url || !config) return ''

  const [base, query = ''] = url.split('/?')
  const params = new URLSearchParams(query)
  const tables = historyTablesOf(config)

  const where = parseWhere(params.get('where'))
  Object.keys(where).forEach((column) => {
    if (tables.includes(tableOf(column))) delete where[column]
  })
  if (Object.keys(where).length) params.set('where', JSON.stringify(where))
  else params.delete('where')
  paramsOnTables(datasetKey, tables).forEach(param => params.delete(param))

  params.set('view', 'json')
  params.set('columns', columns)
  params.set('limit', '-1')
  params.set('plaintext', 'true')
  params.set('live', 'false')
  ;['by', 'freq', 'stack', 'orientation', 'legendPosition', 'innerRadius', 'height',
    'REFRESH_INTERVAL', 'engine', 'mapping', 'trendline', 'with_tables', 'except_columns',
    'qualified_columns',
  ].forEach(key => params.delete(key))

  return `${base}/?${params.toString().replace(/%2C/gi, ',')}`
}

/** The rows of one history (record id + history row id) of the records in scope. */
export function buildHistoryRowsUrl(url, datasetKey, config, history) {
  return buildScopeReadUrl(url, datasetKey, config, `${config?.recordColumn},${history?.historyColumn}`)
}

/**
 * The records in scope, with or without history. The json view returns
 * identical rows once, so this is one row per record (plus one for rows that
 * reach no record at all).
 */
export function buildRecordsUrl(url, datasetKey, config) {
  return buildScopeReadUrl(url, datasetKey, config, config?.recordColumn)
}

/**
 * The latest history row of each record, by id: the API's rule
 * (`last_status()` and friends order by id; for the datasets ordered by
 * created_at it was measured to give the same row). Records with no history
 * row are skipped; the read keys its rows by the qualified column names.
 */
export function pickLatestRowIds(rows, config, history) {
  if (!Array.isArray(rows) || !config || !history) return []

  const latest = new Map()
  rows.forEach((row) => {
    const record = readColumn(row, config.recordColumn)
    const rawId = readColumn(row, history.historyColumn)
    const id = Number(rawId)
    if (record === null || record === undefined) return
    if (rawId === null || rawId === undefined || rawId === '' || !Number.isFinite(id)) return
    if (!latest.has(record) || id > latest.get(record)) latest.set(record, id)
  })

  return [...latest.values()].sort((a, b) => a - b)
}

/**
 * Let only the latest rows of the given histories join. `latest` maps each
 * history column to its ids. The ids travel as numbers (measured: accepted,
 * and shorter than quoted strings). An empty list answers HTTP 500, so a
 * history without ids is left unrestricted.
 */
export function restrictToLatestRows(params, histories, latest) {
  const where = parseWhere(params.get('where'))
  let restricted = false
  histories.forEach(({ historyColumn }) => {
    const ids = latest?.[historyColumn]
    if (!ids?.length) return
    where[historyColumn] = { operator: 'in', values: ids }
    restricted = true
  })
  if (restricted) params.set('where', JSON.stringify(where))
}

/**
 * «Solo abiertos»: leave out the records whose current status is one of
 * `closed.values`. `notin` is the service's operator (`not in` answers HTTP
 * 500, measured); a filter the chart already has on that column is kept next
 * to it, as an array (AND). Records with no status drop out as well: no
 * operator keeps NULLs. On a status read from a history this only counts the
 * current one together with restrictToLatestRows.
 */
export function excludeClosedStatuses(params, closed) {
  if (!closed?.column || !closed.values?.length) return
  const where = parseWhere(params.get('where'))
  const condition = { operator: 'notin', values: closed.values }
  const existing = where[closed.column]
  where[closed.column] = existing ? [].concat(existing, condition) : condition
  params.set('where', JSON.stringify(where))
}

/** A URL without the restriction, e.g. a download built from a restricted chart. */
export function removeLatestRowsRestriction(url, config) {
  const [base, query = ''] = url.split('/?')
  const params = new URLSearchParams(query)
  const where = parseWhere(params.get('where'))
  const columns = historiesOf(config).map(history => history.historyColumn).filter(column => column in where)
  if (!columns.length) return url
  columns.forEach((column) => { delete where[column] })
  if (Object.keys(where).length) params.set('where', JSON.stringify(where))
  else params.delete('where')
  return `${base}/?${params.toString().replace(/%2C/gi, ',')}`
}

/** Same as restrictToLatestRows, on a full URL. */
export function restrictUrlToLatestRows(url, histories, latest) {
  const [base, query = ''] = url.split('/?')
  const params = new URLSearchParams(query)
  restrictToLatestRows(params, histories, latest)
  return `${base}/?${params.toString().replace(/%2C/gi, ',')}`
}
